import { useModelLabel } from "@judo/i18n";
import type { Checkbox as CheckboxElement, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText } from "@mui/material";

export interface CheckboxComponentProps {
	element: CheckboxElement | Input;
	readOnly?: boolean;
	value?: boolean;
	onChange?: (value: boolean) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * Checkbox input component.
 * Matches template pattern: FormControl > FormGroup > FormControlLabel with marginTop and error coloring.
 */
export function CheckboxComponent({
	element,
	readOnly,
	value = false,
	onChange,
	error,
	disabled,
}: CheckboxComponentProps) {
	const testId = getInputTestId(element);
	const { label: modelLabel } = useModelLabel(element);

	const label = `${modelLabel}${element.required ? " *" : ""}`;
	const hasError = !!error;

	return (
		<FormControl error={hasError} disabled={disabled ?? element.disabled} required={element.required}>
			<FormGroup>
				<FormControlLabel
					className="checkbox"
					sx={{
						marginTop: "6px",
						color: hasError ? "error.main" : undefined,
					}}
					control={
						<Checkbox
							checked={value}
							onChange={(e) => onChange?.(e.target.checked)}
							disabled={readOnly ?? element.isReadOnly}
							data-testid={testId}
							sx={{
								color: hasError ? "error.main" : undefined,
							}}
						/>
					}
					label={label}
				/>
			</FormGroup>
			{hasError && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}
